import React from 'react';
import { IntlShape } from 'react-intl';
import Link from '@atlaskit/link';

import { PASSWORD_LEAKED_LINK } from '../../../components/links';
import { LoginGlobalError } from '../../../types';
import { Application } from '../../../utilities/applications';
import { isFedM } from '../../../utilities/env';
import { ResetPasswordLink } from '../components/ResetPasswordLink';

type BuildLoginErrorMessageArgs = {
  error: LoginGlobalError | null;
  formatMessage: IntlShape['formatMessage'];
  signupLink: string;
  nextAttempt: number;
  application: Application | undefined;
};

export const buildLoginErrorMessage = ({
  error,
  formatMessage,
  signupLink,
  nextAttempt,
  application,
}: BuildLoginErrorMessageArgs): React.ReactNode => {
  if (!error) return;

  const { args = {} } = error;

  switch (error?.code) {
    case 'captcha':
      return formatMessage({
        id: 'login.error.captcha',
        defaultMessage:
          'We are having trouble verifying reCAPTCHA for this request. Please try again. If the problem persists, try another browser/device or reach out to Atlassian Support.',
      });
    case 'captchaScoreTooLow':
      return formatMessage({
        id: 'login.error.captcha.score_too_low',
        defaultMessage:
          "We'd like to make sure it's really you. Please confirm you're not a robot.",
        description: 'Message asking user to tick the reCAPTCHA checkbox and resubmit the form',
      });
    case 'invalid_user_password':
      return incorrectCredentialsErrorMessage(formatMessage, nextAttempt, signupLink, application);
    case 'ambiguous_user':
      return formatMessage({
        id: 'login.error.details.ambiguous',
        defaultMessage:
          'It seems that you have multiple accounts associated with your email. Please contact your administrator to remove the duplicate.',
      });
    case 'no_email_on_remote_user':
      return formatMessage(
        {
          id: 'login.error.remote.user.no.email',
          defaultMessage:
            "Your {username} user doesn't have an associated email address, so we can't create your Atlassian account. Please contact support.",
        },
        {
          ...args,
        }
      );
    case 'bitbucket_username_is_not_email':
      return formatMessage(
        {
          id: 'login.error.remote.user.using.username',
          defaultMessage:
            'You can no longer log in with a username. Use your primary email address {username} instead.',
        },
        {
          ...args,
        }
      );
    case 'username_is_not_email':
      return formatMessage(
        {
          id: 'login.error.email.not.username',
          defaultMessage:
            'You must use your email address to log in. Did you mean to use {username}?',
        },
        {
          ...args,
        }
      );
    case 'bitbucket_email_is_not_primary':
      return formatMessage(
        {
          id: 'login.error.remote.user.using.alias',
          defaultMessage:
            'You can no longer log in with an email alias. Use your primary email address {username} instead.',
        },
        {
          ...args,
        }
      );
    case 'bitbucket_mfa_enforced':
      return formatMessage(
        {
          id: 'login.error.user.mfa.enforced',
          defaultMessage:
            'Your organization wants you to secure your account by setting up two-step verification.',
        },
        {
          ...args,
        }
      );
    case 'bitbucket_mfa_required':
      return formatMessage(
        {
          id: 'login.error.user.mfa.required',
          defaultMessage:
            'For added security, your organization wants you to log in with two-step verification.',
        },
        {
          ...args,
        }
      );
    case 'bitbucket_saml_user':
      return formatMessage(
        {
          id: 'login.error.saml.user',
          defaultMessage:
            'For added security, your organization wants you to log in with single sign-on.',
        },
        { ...args }
      );
    case 'email_is_not_primary':
      return formatMessage(
        {
          id: 'login.error.email.not.username',
          defaultMessage:
            'You must use your email address to log in. Did you mean to use {username}?',
        },
        {
          ...args,
        }
      );
    case 'too_many_logins':
    case 'too_many_requests':
      return formatMessage({
        id: 'login.error.too.many.logins',
        defaultMessage:
          "For security purposes, we've temporarily locked your account due to multiple log in attempts within a short period of time. Please try again in a few minutes. If this issue persists, please contact Atlassian Support.",
      });
    case 'multiple_google_accounts':
      return formatMessage({
        id: 'login.error.google.auth.contact.support',
        defaultMessage:
          "We've encountered problems signing you in with your Google Account. Please contact support to resolve this problem.",
      });
    case 'unable_to_verify_google_token':
      return formatMessage({
        id: 'login.error.unable.to.verify.google.token',
        defaultMessage: 'We were unable to verify your Google Account. Please try again later.',
      });
    case 'google_email_change_not_allowed_by_auth_policy':
      return formatMessage({
        id: 'login.error.google.email.change.not.allowed.by.auth.policy',
        defaultMessage:
          "Sorry, we detected an email change that is not supported by your organization's authentication policy. Please log in with an email address that matches your account.",
      });
    case 'google_email_change_not_allowed_by_user_managed_status':
      return formatMessage({
        id: 'login.error.google.email.change.not.allowed.by.user.managed.status',
        defaultMessage:
          'We detected an email change that would remove you from your organization. Contact your organization admin to check your account is configured correctly or try to log in a different way.',
      });
    case 'social_login_missing_email':
      return formatMessage({
        id: 'login.error.social.missing.email',
        defaultMessage:
          'We did not receive a valid email address from your identity provider. Please try logging in again by typing your email address. If you don\'t have a password, use the "Can\'t log in" link to set one.',
      });
    case 'social_login_missing_email_verified':
      return formatMessage({
        id: 'login.error.social.missing.email.verified',
        defaultMessage:
          'We did not receive a valid email verification from your identity provider. Please try logging in again by typing your email address. If you don\'t have a password, use the "Can\'t log in" link to set one.',
      });
    case 'social_login_unexpected_login_type':
      return formatMessage({
        id: 'login.error.social.unexpected.login.type',
        defaultMessage:
          'We did not receive valid information from your identity provider. Please try logging in again by typing your email address. If you don\'t have a password, use the "Can\'t log in" link to set one.',
      });
    case 'social_login_missing_slack_email_date_verified':
      return formatMessage({
        id: 'login.error.slack.missing.email.date.verified',
        defaultMessage:
          'We did not receive a valid email date verified from your identity provider. Please try logging in again by typing your email address. If you don\'t have a password, use the "Can\'t log in" link to set one.',
      });
    case 'social_login_invalid_social_id':
      return formatMessage({
        id: 'login.error.social.invalid.social.id',
        defaultMessage:
          'We did not receive valid id from your identity provider. Please try logging in again by typing your email address. If you don\'t have a password, use the "Can\'t log in" link to set one.',
      });
    case 'slack_login_invalid_team_id':
      return formatMessage({
        id: 'login.error.slack.invalid.team.id',
        defaultMessage:
          'We did not receive valid team id from your identity provider. Please try logging in again by typing your email address. If you don\'t have a password, use the "Can\'t log in" link to set one.',
      });
    case 'social_login_managed_user':
      return formatMessage({
        id: 'login.error.social.managed.user',
        defaultMessage:
          "Hmm... we're having trouble logging you in. Please try again with a different authentication method.",
      });
    case 'social_login_requires_password_change':
      return formatMessage({
        id: 'login.error.social.requires.password.change',
        defaultMessage:
          'Your current password has expired, so you need to reset your password to continue.',
      });
    case 'social_login_hide_email_change_not_allowed_to_bypass_policy':
      return formatMessage({
        id: 'login.error.social.hide.email.change.not.allowed.to.bypass.policy',
        defaultMessage:
          'Sorry, you cannot hide your email once you are part of an organization. Please ensure that you share your real email.',
      });
    case 'social_login_email_change_not_allowed_to_bypass_policy':
      return formatMessage({
        id: 'login.error.social.email.change.not.allowed.to.bypass.policy',
        defaultMessage:
          "Sorry, we detected an email change that is not supported by your organization's authentication policy. Please log in with a email address that matches your account.",
      });
    case 'social_login_email_change_not_allowed':
      return formatMessage({
        id: 'login.error.social.email.change.not.allowed',
        defaultMessage:
          'We detected an email change that would remove you from your organization. Contact your organization admin to check your account is configured correctly or try to log in a different way.',
      });
    case 'social_login_consent_required':
      return formatMessage({
        id: 'login.error.social.login.consent.required',
        defaultMessage:
          'Please grant access to your account details if you wish to login to your Atlassian account.',
      });
    case 'user_is_blocked':
      return formatMessage({
        id: 'login.error.user.blocked',
        defaultMessage:
          'This account has been deactivated. Please contact your organization admin.',
      });
    case 'social_login_banned':
      return formatMessage({
        id: 'login.error.social.login.banned',
        defaultMessage:
          'Your admin has blocked third-party login. Log in with your email address instead.',
      });
    case 'apple_login_disabled':
      return formatMessage({
        id: 'login.error.apple.login.disabled',
        defaultMessage:
          'Sorry, it is not possible to log in with Apple at this time. We are working on it.',
      });
    case 'microsoft_login_disabled':
      return formatMessage({
        id: 'login.error.microsoft.login.disabled',
        defaultMessage:
          'Sorry, it is not possible to log in with Microsoft at this time. We are working on it.',
      });
    case 'slack_login_disabled':
      return formatMessage({
        id: 'login.error.slack.login.disabled',
        defaultMessage:
          'Sorry, it is not possible to log in with Slack at this time. We are working on it.',
      });
    case 'password_leaked':
      return formatMessage(
        {
          id: 'login.error.password.leaked.xml',
          defaultMessage:
            'You must <reset-password-link>change your password</reset-password-link> to log in. Your current password has appeared in an external data breach and your Atlassian account is at risk. <password-leaked-link>Learn more</password-leaked-link>',
        },
        {
          'reset-password-link': chunks => <ResetPasswordLink>{chunks}</ResetPasswordLink>,
          'password-leaked-link': chunks => <a href={PASSWORD_LEAKED_LINK}>{chunks}</a>,
        }
      );
    case 'login.form.token.expired':
      return formatMessage({
        id: 'login.error.token.expired',
        defaultMessage: 'Your login request has expired.',
      });
    case 'login.form.problem.encountered':
      return formatMessage({
        id: 'login.error.problem.encountered',
        defaultMessage: "We've encountered a problem. Please try again in a few minutes.",
      });
    case 'login.form.email.does.not.match.loggedin.user':
      return formatMessage(
        {
          id: 'login.form.email.does.not.match.loggedin.user',
          defaultMessage:
            "You're currently logged in as {username}, logging in as another user will log you out of your current session.",
        },
        {
          ...args,
        }
      );
    case 'hosted_domain_mismatch':
      return formatMessage({
        id: 'google.hosted.domain.mismatch',
        defaultMessage:
          'Unable to log in with Google. Contact your admin to check your account is configured correctly or try to log in a different way.',
      });
    case 'switch.session.expired':
      return formatMessage({
        id: 'session.switch.error.expired',
        defaultMessage: 'Your account session expired. Log in again to continue.',
      });

    case 'request_error':
      return formatMessage({
        id: 'network.request.error',
        defaultMessage:
          'Unable to connect. Check your internet connection or the network settings for your browser and try again.',
      });

    case 'invalid.session.token':
      return formatMessage({
        id: 'invalid.session.token',
        defaultMessage: 'Your session token is invalid. Log in again to continue.',
      });

    case 'verify.email.expired':
      return formatMessage({
        id: 'verify.email.expired',
        defaultMessage:
          'Your email verification attempt has expired. Enter your email address and try again.',
      });

    case 'verify.email.error':
      return formatMessage({
        id: 'verify.email.error',
        defaultMessage:
          'Something went wrong while verifying your email address. Enter your email address and try again.',
      });

    case 'too_many_username_requests':
      return formatMessage({
        id: 'check.username.rate.limit.exceeded',
        defaultMessage:
          'Too many login attempts detected. Please wait for a few minutes before trying again.',
      });

    case 'generic-consent-error':
      return formatMessage({
        id: 'generic-consent-error',
        defaultMessage: 'To access this page, you must accept the Privacy Notice.',
      });

    case 'consent-validation-error':
      return formatMessage({
        id: 'consent-validation-error',
        defaultMessage:
          'Something went wrong while attempting to validate your consent. Try logging in again later.',
      });

    case 'consent-loading-error':
      return formatMessage({
        id: 'consent-loading-error',
        defaultMessage:
          'Something went wrong while loading the consent screen. Try logging in again later.',
      });

    case 'remember-me-cookie-error':
      return formatMessage({
        id: 'remember-me-cookie-error',
        defaultMessage:
          'Something went wrong while loading your remembered accounts. Log in again to continue.',
      });

    case 'invalid_passkey':
      return formatMessage({
        id: 'invalid-passkey-error',
        defaultMessage:
          'The provided passkey is invalid. Please try again with a different authentication method.',
      });
    case 'passkey_managed_account':
      return formatMessage({
        id: 'managed-account-passkey-error',
        defaultMessage:
          "Hmm... we're having trouble logging you in. Please try again with a different authentication method.",
      });
    case 'webauthn_not_supported':
      return formatMessage({
        id: 'webauthn-not-supported-error',
        defaultMessage:
          'We’re unable to verify your passkey. Make sure your browser supports WebAuthn.',
      });
    case 'webauthn_challenge_failed':
      return formatMessage({
        id: 'webauthn-challenge-failed-error',
        defaultMessage:
          'Your passkey couldn’t complete the authentication. Try again. If this keeps happening, contact support.',
      });

    default:
      return formatMessage({
        id: 'login.error.unknown',
        defaultMessage: 'Something went wrong while attempting to validate your credentials.',
      });
  }
};

const incorrectCredentialsErrorMessage = (
  formatMessage: IntlShape['formatMessage'],
  nextAttempt: number,
  signupLink: string,
  application: Application | undefined
) => {
  if (nextAttempt > 4) {
    return formatMessage(
      {
        id: 'login.error.invalid.credentials.more.help.xml',
        defaultMessage:
          "We've recently made changes to the login page. If you are having difficulty logging in, <reset-password-link>reset your password</reset-password-link>.",
      },
      {
        'reset-password-link': chunks => <ResetPasswordLink>{chunks}</ResetPasswordLink>,
      }
    );
  } else if (application === 'trello') {
    return formatMessage(
      {
        id: 'login.error.invalid.credentials.trello.specific.help.xml',
        defaultMessage:
          'Incorrect email address and / or password. If you recently migrated your Trello account to an Atlassian account, you will need to use your Atlassian account password. Alternatively, you can get help <reset-password-link>logging in</reset-password-link>.',
      },
      {
        'reset-password-link': chunks => <ResetPasswordLink>{chunks}</ResetPasswordLink>,
      }
    );
  } else {
    // Fedramp doesn't have a sign up flow, so we don't want to show the sign up link.
    if (isFedM()) {
      return formatMessage(
        {
          id: 'login.error.invalid.credentials.xml',
          defaultMessage:
            'Incorrect email address and/or password. Try again or <reset-password-link>reset your password</reset-password-link>.',
        },
        {
          'reset-password-link': chunks => <ResetPasswordLink>{chunks}</ResetPasswordLink>,
        }
      );
    }
    const invalidCredentials = formatMessage(
      {
        id: 'login.error.invalid.credentials.xml',
        defaultMessage:
          'Incorrect email address and/or password. Try again or <reset-password-link>reset your password</reset-password-link>.',
      },
      {
        'reset-password-link': chunks => <ResetPasswordLink>{chunks}</ResetPasswordLink>,
      }
    );

    const signupSuffix = formatMessage(
      {
        id: 'login.error.invalid.credentials.xml.sign_up',
        defaultMessage: '<sign-up-link>Sign up</sign-up-link> for an Atlassian account.',
      },
      {
        'sign-up-link': chunks => <Link href={signupLink}>{chunks}</Link>,
      }
    );

    return (
      <>
        {invalidCredentials}
        <br />
        {signupSuffix}
      </>
    );
  }
};
